import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import {
	Button,
	DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
	SearchInput,
	Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@rentflowdev/ui'
import { useGetMeQuery } from '@/api'
import type { Manager, Property, PropertyFilters } from '@/types'
import { CREATE_PROPERTY_TYPES } from '@/types'
import { PUBLISH_TARGET_PLATFORMS } from '@/lib/platforms'
import { AddPropertyDialog } from './add-property-dialog'
import { AddPropertyFromAvitoDialog } from './add-property-from-avito-dialog'
import { useCan } from '@/lib/access'

const ALL = 'ALL'
const UNASSIGNED = 'unassigned'

/** Значения, а не пары со строкой: подпись берётся из словаря при отрисовке. */
const ALL_STATUS_OPTIONS: Property['status'][] = ['DRAFT', 'AVAILABLE', 'RENTED', 'ARCHIVED']

interface PropertiesFiltersProps {
	filters: PropertyFilters
	onFilterChange: (patch: Partial<PropertyFilters>) => void
	managers: Manager[]
	showStatus: boolean
	showPlatform: boolean
	showPublished?: boolean
	statusOptions?: Property['status'][]
}

export function PropertiesFilters({ filters, onFilterChange, managers, showStatus, showPlatform, showPublished, statusOptions = ALL_STATUS_OPTIONS }: PropertiesFiltersProps) {
	const { t } = useTranslation()
	const canCreate = useCan('create', 'Property')
	const { data: me } = useGetMeQuery()
	const [searchInput, setSearchInput] = useState(filters.search)
	const [openDialog, setOpenDialog] = useState<'add' | 'avito' | null>(null)

	useEffect(() => {
		// Skip when the input already matches the applied filter — otherwise an
		// unstable onFilterChange identity re-arms the timer on every render.
		if (searchInput === filters.search) return
		const t = setTimeout(() => onFilterChange({ search: searchInput }), 400)
		return () => clearTimeout(t)
	}, [searchInput, filters.search, onFilterChange])

	return (
		<div className="flex items-center justify-between px-4 py-3">
			<div className="flex items-center gap-2">
				<Select
					value={filters.type || ALL}
					onValueChange={(v) => onFilterChange({ type: v === ALL ? '' : v as Property['type'] })}
				>
					<SelectTrigger className="h-8 w-32 gap-1.5 text-sm text-(--color-muted-foreground)">
						<SelectValue placeholder={t('properties.filterType')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL}>{t('properties.allTypes')}</SelectItem>
						{CREATE_PROPERTY_TYPES.map((value) => (
							<SelectItem key={value} value={value}>{t(`properties.types.${value}`)}</SelectItem>
						))}
					</SelectContent>
				</Select>

				<Select
					value={filters.manager || ALL}
					onValueChange={(v) => onFilterChange({ manager: v === ALL ? '' : v })}
				>
					<SelectTrigger className="h-8 w-38 gap-1.5 text-sm text-(--color-muted-foreground)">
						<SelectValue placeholder={t('properties.filterManager')} />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value={ALL}>{t('properties.allManagers')}</SelectItem>
						<SelectItem value={UNASSIGNED}>{t('properties.unassigned')}</SelectItem>
						{managers.map((m) => (
							<SelectItem key={m.id} value={m.id}>
								{m.id === me?.id ? t('properties.me') : m.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>

				{showStatus && (
					<Select
						value={filters.status || ALL}
						onValueChange={(v) => onFilterChange({ status: v === ALL ? '' : v as Property['status'] })}
					>
						<SelectTrigger className="h-8 w-30 gap-1.5 text-sm text-(--color-muted-foreground)">
							<SelectValue placeholder={t('properties.filterStatus')} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ALL}>{t('properties.allStatuses')}</SelectItem>
							{statusOptions.map((value) => (
								<SelectItem key={value} value={value}>{t(`properties.statuses.${value}`)}</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}

				{showPublished && (
					<Select
						value={filters.published || ALL}
						onValueChange={(v) => onFilterChange({ published: v === ALL ? '' : v as PropertyFilters['published'] })}
					>
						<SelectTrigger className="h-8 w-40 gap-1.5 text-sm text-(--color-muted-foreground)">
							<SelectValue placeholder={t('properties.filterPublished')} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ALL}>{t('properties.allPublications')}</SelectItem>
							<SelectItem value="PUBLISHED">{t('properties.published')}</SelectItem>
							<SelectItem value="UNPUBLISHED">{t('properties.unpublished')}</SelectItem>
						</SelectContent>
					</Select>
				)}

				{showPlatform && (
					<Select
						value={filters.platform || ALL}
						onValueChange={(v) => onFilterChange({ platform: v === ALL ? '' : v })}
					>
						<SelectTrigger className="h-8 w-32 gap-1.5 text-sm text-(--color-muted-foreground)">
							<SelectValue placeholder={t('properties.filterPlatform')} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value={ALL}>{t('properties.allPlatforms')}</SelectItem>
							{PUBLISH_TARGET_PLATFORMS.map((value) => (
								<SelectItem key={value} value={value}>{t(`platforms.${value}`)}</SelectItem>
							))}
						</SelectContent>
					</Select>
				)}
			</div>

			<div className="flex items-center gap-2">
				<SearchInput
					placeholder={t('properties.search')}
					value={searchInput}
					className="w-60"
					onChange={(e) => setSearchInput(e.target.value)}
				/>
				{canCreate && (
					<DropdownMenu>
						<DropdownMenuTrigger render={<Button size="sm" iconRight={<ChevronDown className="size-4" />} />}>
							{t('properties.addProperty')}
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end" className="min-w-40">
							<DropdownMenuItem onClick={() => setOpenDialog('add')}>{t('properties.addManually')}</DropdownMenuItem>
							<DropdownMenuItem onClick={() => setOpenDialog('avito')}>{t('properties.importFromAvito')}</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>
				)}
			</div>
			<AddPropertyDialog open={openDialog === 'add'} onOpenChange={(o) => setOpenDialog(o ? 'add' : null)} />
			<AddPropertyFromAvitoDialog open={openDialog === 'avito'} onOpenChange={(o) => setOpenDialog(o ? 'avito' : null)} />
		</div>
	)
}
